import React from 'react';
import cn from 'clsx';
import style from './style.module.scss';
import ConfirmationDialog from '@components-clean/core/confirmation-dialog';
import DraggableDialog from '@components-clean/core/draggable-dialog';
import { InfoField } from '@components-clean/core/info-field';
import Tooltip from '@components-clean/core/tooltip';
import { Attendee } from '@interfaces/report-templates.interface';
import { useTranslation } from '@hooks/index';
import { ShowError, ShowSuccess } from '@utils/notification';
import { useDeleteAttendee, useMutationAttendee } from '@apis/bff-api/report-templates';

export interface DeleteAttendeeDialogProps {
    onClose: () => void;
    onSubmit: () => void;
    attendee: Attendee;
}

const actionTypes = ['delete', 'inactivate', 'deleteInactive'];

const DeleteAttendeeDialog = ({ onClose, onSubmit, attendee }: DeleteAttendeeDialogProps) => {
    const { t } = useTranslation('ram', { keyPrefix: 'manageRiskReviews.dialogs.deleteAttendee' });
    const { t: tCommon } = useTranslation('common');
    const { deleteAttendee } = useDeleteAttendee();
    const { trigger: putAttendee } = useMutationAttendee();

    let selectedAction: number;
    if (!attendee.inUse && attendee.active !== false) {
        selectedAction = 0; // delete
    } else if (attendee.inUse && attendee.active !== false) {
        selectedAction = 1; // inactivate
    } else if (attendee.inUse && attendee.active === false) {
        selectedAction = 2; // already inactive — info only
    } else {
        selectedAction = 0; // not inUse and already inactive — can delete
    }

    const onDelete = async () => {
        const response = await deleteAttendee(attendee.id!);

        if (!Array.isArray(response) || !response[0]) {
            ShowError(tCommon('errorMessage'));
            return;
        }

        ShowSuccess(tCommon('actionsSuccessfulToast'));
        onSubmit();
    };

    const onInactivate = async () => {
        try {
            await putAttendee({
                id: attendee.id,
                firstName: attendee.firstName,
                lastName: attendee.lastName,
                jobTitle: attendee.jobTitle,
                studyId: attendee.studyId,
                active: false,
            });
            ShowSuccess(tCommon('actionsSuccessfulToast'));
            onSubmit();
        } catch {
            ShowError(tCommon('errorMessage'));
        }
    };

    const infoFieldMarkup = (
        <>
            <Tooltip position="top" anchorElement="parent" className={cn(style.tooltipWidth)}>
                <section className={cn(style.deleteName)}>
                    <span className="k-font-medium">{t('delete.attendeeNameLabel')}:</span>{' '}
                    <span title={`${attendee.firstName} ${attendee.lastName}`}>
                        {attendee.firstName} {attendee.lastName}
                    </span>
                </section>
            </Tooltip>

            <Tooltip position="top" anchorElement="parent" className={cn(style.tooltipWidth)}>
                <section className={cn(style.deleteJobTitle)}>
                    <span className="k-font-medium">{t('delete.jobTitleLabel')}:</span>{' '}
                    <span title={attendee.jobTitle ?? ''}>{attendee.jobTitle}</span>
                </section>
            </Tooltip>
        </>
    );

    return (
        <>
            {selectedAction === 0 && (
                <DraggableDialog
                    dataTestId="delete-attendee-dialog"
                    onSubmit={onDelete}
                    onClose={onClose}
                    title={`${t(`${actionTypes[selectedAction]}.title`)}: ${attendee._itemId ?? attendee.recordId}`}
                    initialWidth={530}
                    initialHeight={280}
                    height={null}
                    primaryBtnLabel={tCommon('delete')}
                >
                    <div className={cn(style.deleteAttendee, 'k-flex k-w-full k-h-full')}>
                        <InfoField type="Warning" message={infoFieldMarkup} />
                        <div className={cn(style.deletePromptDiv, 'k-w-12/12 k-text-center')}>
                            <span>{t('delete.message')}</span>
                        </div>
                    </div>
                </DraggableDialog>
            )}
            {selectedAction === 1 && (
                <ConfirmationDialog
                    title={`${t(`${actionTypes[selectedAction]}.title`)}: ${attendee._itemId ?? attendee.recordId}`}
                    dataTestId="inactivate-attendee-dialog"
                    onClose={onClose}
                    onSubmit={onInactivate}
                    primaryBtnLabel={tCommon('confirm')}
                    className="info"
                >
                    <div>
                        <div style={{ width: '423px', margin: '0 0', marginBottom: '30px' }}>
                            <InfoField type="Info" message={t('inactivate.infoMessage')} />
                        </div>
                        <div style={{ textAlign: 'center', marginBottom: '18px' }}>{t('inactivate.message')}</div>
                    </div>
                </ConfirmationDialog>
            )}
            {selectedAction === 2 && (
                <ConfirmationDialog
                    title={`${t(`${actionTypes[selectedAction]}.title`)}: ${attendee._itemId ?? attendee.recordId}`}
                    dataTestId="delete-inactive-attendee-dialog"
                    onClose={onClose}
                    secondaryBtnLabel={tCommon('close')}
                >
                    <div>
                        <div>{t('deleteInactive.message')}</div>
                    </div>
                </ConfirmationDialog>
            )}
        </>
    );
};

export default DeleteAttendeeDialog;
